import React, { useRef, useState, useEffect } from 'react';
import cx from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import LoadingIndicator from '@magoosh/layouts/components/loading_indicator';
import { DangerAlert } from '@magoosh/lib/alerts';
import { SuccessBadge, WarningBadge } from '@magoosh/lib/badge';
import { DefaultButton, GradientButton } from '@magoosh/lib/button';
import Collapse from '@magoosh/lib/collapse';
import { FontAwesomeIcon, SparkleIcon } from '@magoosh/lib/icons';
import { paths } from 'config/path_helpers';
import { pluralize } from 'utilities/pluralize';

import ContextualLink from '@test_prep/app/components/contextual_link';

import Facet from '../facet';
import NoContent from '../no_content';
import ScoreInfluenceAwareContent from '../score_influence_aware_content';
import { Answer, ScoreReportResponse } from '../../types';

import colors from '@magoosh/lib/styles/colors.module.scss';
import style from './style.module.scss';

interface Props {
  analysisExpandedByDefault?: boolean;
  displayStimulusImage?: boolean;
  offerRetry?: boolean;
  regrade?: (response: ScoreReportResponse) => void;
  response: ScoreReportResponse;
  scoreBadge?: {
    text: string;
    type: 'positive' | 'negative';
  };
  title?: string;
  volume: number;
  setVolume: (volume: number) => void;
}

export default (props: Props) => {
  const { answerId } = useParams();
  const promptAudioRef = useRef<HTMLAudioElement | null>(null);
  const responseAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlayingPrompt, setIsPlayingPrompt] = useState(false);
  const [isPlayingResponse, setIsPlayingResponse] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  const promptAudio = props.response.answer.prompt.content.audio;
  const responseAudio = props.response.answer.response.audio;

  useEffect(() => {
    // Cleanup audio on unmount
    return () => {
      if (promptAudioRef.current) {
        promptAudioRef.current.pause();
        promptAudioRef.current = null;
      }
      if (responseAudioRef.current) {
        responseAudioRef.current.pause();
        responseAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Update volume for both audio elements when volume changes
    if (promptAudioRef.current) {
      promptAudioRef.current.volume = props.volume;
    }
    if (responseAudioRef.current) {
      responseAudioRef.current.volume = props.volume;
    }
  }, [props.volume]);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.setVolume(parseFloat(e.target.value));
  };

  const toggleMute = () => {
    props.setVolume(props.volume === 0 ? 1 : 0);
  };

  const getVolumeIcon = () => {
    if (props.volume === 0) return 'volume-off';
    if (props.volume < 0.5) return 'volume-down';
    return 'volume-up';
  };

  const playPromptAudio = () => {
    if (!promptAudioRef.current) {
      promptAudioRef.current = new Audio(promptAudio.url);
      promptAudioRef.current.volume = props.volume;
      promptAudioRef.current.addEventListener('ended', () => setIsPlayingPrompt(false));
      promptAudioRef.current.addEventListener('pause', () => setIsPlayingPrompt(false));
    }

    if (isPlayingPrompt) {
      promptAudioRef.current.pause();
      promptAudioRef.current.currentTime = 0;
      setIsPlayingPrompt(false);
    } else {
      // Pause response audio if playing
      if (responseAudioRef.current && isPlayingResponse) {
        responseAudioRef.current.pause();
        responseAudioRef.current.currentTime = 0;
        setIsPlayingResponse(false);
      }

      promptAudioRef.current.play();
      setIsPlayingPrompt(true);
    }
  };

  const playResponseAudio = () => {
    if (!responseAudioRef.current) {
      responseAudioRef.current = new Audio(responseAudio.url);
      responseAudioRef.current.volume = props.volume;
      responseAudioRef.current.addEventListener('ended', () => setIsPlayingResponse(false));
      responseAudioRef.current.addEventListener('pause', () => setIsPlayingResponse(false));
    }

    if (isPlayingResponse) {
      responseAudioRef.current.pause();
      responseAudioRef.current.currentTime = 0;
      setIsPlayingResponse(false);
    } else {
      // Pause prompt audio if playing
      if (promptAudioRef.current && isPlayingPrompt) {
        promptAudioRef.current.pause();
        promptAudioRef.current.currentTime = 0;
        setIsPlayingPrompt(false);
      }

      responseAudioRef.current.play();
      setIsPlayingResponse(true);
    }
  };

  return (
    <div className="d-flex flex-column gap-m">
      <div className="align-items-center d-flex gap-m gap-lg-xl">
        <h4
          className="flex-1 u-margin-A-n"
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          <strong>
            {props.title || promptAudio?.transcript || 'Response'}
          </strong>
        </h4>

        <div className="align-items-center d-flex gap-s">
          {
            props.scoreBadge && (
              props.scoreBadge.type === 'positive' ? (
                <div><SuccessBadge>{props.scoreBadge.text}</SuccessBadge></div>
              ) : (
                <div><WarningBadge>{props.scoreBadge.text}</WarningBadge></div>
              )
            )
          }

          <h4 className="u-margin-A-n">
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="response-score-tooltip">
                  Scores range from {props.response.scoreRange.min} to {props.response.scoreRange.max}
                </Tooltip>
              }
            >
              <span className="text-underdash">Score <strong>{props.response.score}</strong></span>
            </OverlayTrigger>
          </h4>
        </div>
      </div>

      <div
        className="d-flex gap-l u-border-A-s u-padding-A-s"
        style={{ borderRadius: 8 }}
      >
        {
          props.displayStimulusImage && (
            <div className="flex-none hidden-sm hidden-xs">
              <img
                alt=""
                src={props.response.answer.prompt.content.structuredData.props.stimulus.referenceImageSrc}
                width="200"
              />
            </div>
          )
        }

        <div className="d-flex flex-1 flex-column gap-m">
          <div className="align-items-sm-center d-flex flex-column flex-sm-row gap-s">
            <div className="align-items-center d-flex gap-s">
              {
                promptAudio && (
                  <DefaultButton size="xs" onClick={playPromptAudio}>
                    <FontAwesomeIcon icon={isPlayingPrompt ? "pause" : "play"} /> Prompt
                  </DefaultButton>
                )
              }

              <DefaultButton size="xs" onClick={playResponseAudio}>
                <FontAwesomeIcon icon={isPlayingResponse ? "pause" : "play"} /> <span className="hidden-xs">Your </span>Response
              </DefaultButton>

              <div className="d-flex align-items-center gap-xs">
                <div
                  className="d-flex justify-content-center"
                  onClick={() => setShowVolumeSlider(!showVolumeSlider)}
                  style={{ cursor: 'pointer', width: '16px' }}
                >
                  <FontAwesomeIcon icon={getVolumeIcon()} />
                </div>

                {showVolumeSlider && (
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={props.volume}
                    onChange={handleVolumeChange}
                    style={{ width: '70px', cursor: 'pointer' }}
                    title={`Volume: ${Math.round(props.volume * 100)}%`}
                  />
                )}
              </div>
            </div>

            {
              props.offerRetry && (
                <div className="d-flex flex-1 justify-content-sm-end">
                  <ContextualLink
                    className="text-hover-no-decoration text-no-decoration"
                    href={answerId
                      ? `${paths.scoreReportRetryAnswer(props.response.gradingRequestId, props.response.answer.id)}?return_to_answer_id=${answerId}`
                      : paths.scoreReportRetryAnswer(props.response.gradingRequestId, props.response.answer.id)
                    }
                  >
                    <GradientButton size="sm">
                      <SparkleIcon color="#0577E9" size={12} /> <span className="text-bold tiny">Try Again</span>
                    </GradientButton>
                  </ContextualLink>
                </div>
              )
            }
          </div>

          {(() => {
            if (props.response.status === 'finished') {
              return (
                <>
                  <ScoreInfluenceAwareContent scoreInfluence={props.response.scoreInfluence}>
                    {
                      props.response.scoreInfluence === 'promoter' && (
                        <div className="small text-bold">Strong</div>
                      )
                    }
                    {
                      props.response.scoreInfluence === 'detractor' && (
                        <div className="small text-bold">Detractor</div>
                      )
                    }

                    <div className="small">
                      {props.response.content.narrative}
                    </div>
                  </ScoreInfluenceAwareContent>

                  <div>
                    <Collapse defaultExpanded={props.analysisExpandedByDefault}>
                      <Collapse.Trigger>
                        {({ isExpanded }) => {
                          return (
                            <div className="align-items-center d-flex flex-1 gap-xs small u-margin-B-s">
                              <Collapse.Chevron isExpanded={isExpanded} />

                              <div className="text-bold">
                                {isExpanded ? 'Hide' : 'Show'} analysis
                              </div>

                              
                            </div>
                          );
                        }}
                      </Collapse.Trigger>

                      <Collapse.Content>
                        <div className="d-flex flex-column gap-m">
                          {
                            props.response.content.facets && (
                              <div className="d-flex flex-column gap-s">
                                <div>Vocal facets</div>

                                {
                                  props.response.content.facets.map((facet) => (
                                    <Facet
                                      name={facet.name}
                                      range={facet.range}
                                      tooltip={facet.hint}
                                      value={facet.value}
                                    />
                                  ))
                                }
                              </div>
                            )
                          }

                          <div className="d-flex flex-column gap-s u-padding-Hs">
                            <div>Transcript</div>

                            <div className="small">
                              <strong>Yours:</strong> {responseAudio.transcript}
                            </div>

                            {
                              promptAudio && (
                                <div className="small">
                                  <strong>Prompt:</strong> {promptAudio.transcript}
                                </div>
                              )
                            }
                          </div>
                        </div>
                      </Collapse.Content>
                    </Collapse>
                  </div>
                </>
              );
            } else if (props.response.status === 'processing') {
              return (
                <div
                  className="d-flex align-items-center justify-content-center u-padding-A-m"
                  style={{ height: '100%', width: '100%' }}
                >
                  <LoadingIndicator />
                </div>
              );
            } else if (props.response.status === 'error') {
              return (
                <div
                  className="d-flex align-items-center justify-content-center u-padding-B-m"
                  style={{ height: '100%', width: '100%' }}
                >
                  <small>
                    <FontAwesomeIcon icon="exclamation-triangle" />
                    {' '}Something went wrong while scoring this response.
                    {
                      props.regrade && (
                        <>
                          {' '}
                          <a className="text-bold text-body" onClick={() => props.regrade(props.response)}>Retry scoring</a>.
                        </>
                      )
                    }
                  </small>
                </div>
              );
            } else {
              return <NoContent />;
            }
          })()}
        </div>
      </div>
    </div>
  );
};
